import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Car, Pencil, ExternalLink, Link2 } from 'lucide-react';
import { getTenants, getProperties, getUnits, getParkingAssignments, updateTenant } from '@/lib/api';
import { getBankTransactions } from '@/lib/bankTransactionsApi';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const fmtDate = (v) => {
  if (!v) return '-';
  try {
    const d = new Date(v + 'T00:00:00');
    return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${String(d.getFullYear()).slice(-2)}`;
  } catch { return v; }
};
const fmtMoney = (v) => v != null && v !== '' && v !== 0 ? `$${parseFloat(v).toLocaleString()}` : '-';

let cachedTenants = null;
let cachedProps = null;
let cachedUnits = null;

async function ensureCache() {
  if (!cachedTenants) cachedTenants = await getTenants();
  if (!cachedProps) cachedProps = await getProperties();
  if (!cachedUnits) cachedUnits = await getUnits();
  return { tenants: cachedTenants, props: cachedProps, units: cachedUnits };
}

export function invalidateTenantCache() {
  cachedTenants = null;
  cachedProps = null;
  cachedUnits = null;
}

export function TenantDetailModal({ tenantId, tenantData, open, onClose }) {
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(tenantData || null);
  const [propName, setPropName] = useState('');
  const [unitNum, setUnitNum] = useState('');
  const [loading, setLoading] = useState(false);
  const [parkingAssignments, setParkingAssignments] = useState([]);
  const [linkedTxns, setLinkedTxns] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (tenantData) {
      setTenant(tenantData);
      return;
    }
    if (!tenantId) return;

    setLoading(true);
    ensureCache().then(({ tenants, props, units }) => {
      const t = tenants.find(t => t.id === tenantId);
      if (t) {
        setTenant(t);
        const prop = props.find(p => p.id === t.property_id);
        const unit = units.find(u => u.id === t.unit_id);
        setPropName(prop?.name || prop?.address || '');
        setUnitNum(unit?.unit_number || '');
      }
      setLoading(false);
    });
  }, [open, tenantId, tenantData]);

  useEffect(() => {
    if (tenant && !propName) {
      ensureCache().then(({ props, units }) => {
        const prop = props.find(p => p.id === tenant.property_id);
        const unit = units.find(u => u.id === tenant.unit_id);
        setPropName(prop?.name || prop?.address || '');
        setUnitNum(unit?.unit_number || '');
      });
    }
  }, [tenant, propName]);

  useEffect(() => {
    const tid = tenantId || tenantData?.id;
    if (open && tid) {
      getParkingAssignments({ tenant_id: tid }).then(setParkingAssignments).catch(() => {});
      getBankTransactions({ linked_tenant_id: tid })
        .then(txns => setLinkedTxns((txns || []).sort((a, b) => (b.date || '').localeCompare(a.date || ''))))
        .catch(() => setLinkedTxns([]));
    } else {
      setParkingAssignments([]);
      setLinkedTxns([]);
    }
  }, [open, tenantId, tenantData?.id]);

  const isAirbnb = tenant?.is_airbnb_vrbo;

  const startEdit = () => { setForm({ ...tenant }); setEditing(true); };
  const cancelEdit = () => { setForm({}); setEditing(false); };
  const confirmSave = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      await updateTenant(tenant.id, form);
      toast.success('Tenant updated');
      setEditing(false);
      invalidateTenantCache();
      // Reload
      const { tenants, props, units } = await ensureCache();
      const t = tenants.find(t => t.id === (tenantId || tenant.id));
      if (t) setTenant(t);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };
  const goToTenant = () => { onClose(); navigate(`/tenants?highlight=${tenant?.id}`); };

  return (
    <>
    <Dialog open={open} onOpenChange={() => { setEditing(false); onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="tenant-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">{tenant?.name || 'Tenant Details'}</DialogTitle>
          <DialogDescription>
            {propName && unitNum ? `${propName} - Unit ${unitNum}` : 'Loading...'}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-muted-foreground text-sm">Loading...</div>}

        {tenant && !loading && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant={isAirbnb ? 'secondary' : 'default'}>
                {isAirbnb ? 'Airbnb/VRBO' : 'Long-term'}
              </Badge>
              {!isAirbnb && tenant.is_m2m && (
                <Badge className="text-xs bg-amber-100 text-amber-900 border border-amber-200">M2M</Badge>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Move In</p>
                <p className="text-sm tabular-nums">{fmtDate(tenant.move_in_date)}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Move Out</p>
                <p className="text-sm tabular-nums">{fmtDate(tenant.move_out_date)}</p>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Phone</p>
                <p className="text-sm">{tenant.phone || '-'}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Email</p>
                <p className="text-sm break-all">{tenant.email || '-'}</p>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              {!isAirbnb && (
                <>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Monthly Rent</p>
                    <p className="text-sm tabular-nums font-medium">{fmtMoney(tenant.monthly_rent)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Payment Method</p>
                    <p className="text-sm">{tenant.payment_method || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Rent Due</p>
                    <p className="text-sm">{tenant.rent_due_date || '-'}</p>
                  </div>
                </>
              )}
              {isAirbnb && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Rent</p>
                  <p className="text-sm tabular-nums font-medium">{fmtMoney(tenant.total_rent)}</p>
                </div>
              )}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Deposit Amount</p>
                <p className="text-sm tabular-nums">{fmtMoney(tenant.deposit_amount)}</p>
              </div>
            </div>

            {(tenant.deposit_return_date) && (
              <>
                <Separator />
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Deposit Returned</p>
                    <p className="text-sm tabular-nums">{fmtDate(tenant.deposit_return_date)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Return Amount</p>
                    <p className="text-sm tabular-nums">{fmtMoney(tenant.deposit_return_amount)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Return Method</p>
                    <p className="text-sm">{tenant.deposit_return_method || '-'}</p>
                  </div>
                </div>
              </>
            )}

            {linkedTxns.length > 0 && (() => {
              const netTotal = linkedTxns.reduce((s, t) => s + (t.amount || 0), 0);
              return (
                <>
                  <Separator />
                  <div data-testid="tenant-linked-txns">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        <Link2 className="h-3 w-3 text-sky-700" />
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Linked Bank Transactions</p>
                        <Badge variant="secondary" className="text-[10px] font-normal h-4 px-1.5">{linkedTxns.length}</Badge>
                      </div>
                      <div className="text-right">
                        <p className="text-[9px] uppercase tracking-wide text-muted-foreground">Net</p>
                        <p className={`text-xs font-semibold tabular-nums ${netTotal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {netTotal >= 0 ? '+' : '-'}{fmtMoney(Math.abs(netTotal))}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1 rounded-md border border-border/50 bg-[#faf8f4]/60 p-2">
                      {linkedTxns.map((txn) => {
                        const isReturn = (txn.amount || 0) < 0;
                        return (
                          <div
                            key={txn.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded bg-white border border-border/40 text-xs"
                            data-testid="tenant-linked-txn-row"
                          >
                            <span className="tabular-nums text-muted-foreground flex-shrink-0 w-14">{fmtDate(txn.date)}</span>
                            <span className="flex-1 min-w-0 truncate">
                              {txn.title ? <span className="font-medium">{txn.title}</span> : <span className="italic text-muted-foreground">{txn.description || '—'}</span>}
                            </span>
                            <Badge variant="outline" className="text-[9px] font-normal flex-shrink-0 h-4 px-1.5">
                              {isReturn ? 'Returned' : 'Received'}
                            </Badge>
                            <span className={`tabular-nums font-semibold flex-shrink-0 w-20 text-right ${isReturn ? 'text-red-700' : 'text-emerald-700'}`}>
                              {isReturn ? '-' : '+'}{fmtMoney(Math.abs(txn.amount || 0))}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}

            {(tenant.pets || tenant.notes) && (
              <>
                <Separator />
                {tenant.pets && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pets</p>
                    <p className="text-sm">{tenant.pets}</p>
                  </div>
                )}
                {tenant.notes && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{tenant.notes}</p>
                  </div>
                )}
              </>
            )}

            {parkingAssignments.length > 0 && (
              <>
                <Separator />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Parking</p>
                  <div className="space-y-1.5">
                    {parkingAssignments.map(pa => (
                      <div key={pa.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded bg-violet-50 border border-violet-200" data-testid="tenant-parking-note">
                        <Car className="h-3.5 w-3.5 text-violet-600 flex-shrink-0" />
                        <span className="font-medium text-violet-800">{pa.spot_label}</span>
                        <span className="text-xs text-violet-600">{pa.start_date} to {pa.end_date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Edit inline fields */}
            {editing && (
              <>
                <Separator />
                <div className="space-y-2 pt-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Edit Fields</p>
                  {[
                    { key: 'name', label: 'Name', type: 'text' },
                    { key: 'phone', label: 'Phone', type: 'text' },
                    { key: 'email', label: 'Email', type: 'text' },
                    { key: 'move_in_date', label: 'Move In', type: 'date' },
                    { key: 'move_out_date', label: 'Move Out', type: 'date' },
                    { key: 'monthly_rent', label: 'Monthly Rent', type: 'number' },
                    { key: 'notes', label: 'Notes', type: 'text' },
                  ].map(f => (
                    <div key={f.key}>
                      <Label className="text-xs text-muted-foreground">{f.label}</Label>
                      <Input
                        type={f.type}
                        value={form[f.key] || ''}
                        onChange={e => setForm(p => ({...p, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || '' : e.target.value }))}
                        className="h-7 text-sm mt-0.5"
                        data-testid={`tenant-modal-edit-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <Separator className="mt-2" />
        <DialogFooter className="flex-row gap-2 sm:justify-between pt-2">
          <Button variant="outline" size="sm" onClick={goToTenant} className="gap-1.5" data-testid="tenant-modal-go-to">
            <ExternalLink className="h-3.5 w-3.5" /> Go to Tenant
          </Button>
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button variant="ghost" size="sm" onClick={cancelEdit}>Cancel</Button>
                <Button size="sm" onClick={() => setShowConfirm(true)} disabled={saving} data-testid="tenant-modal-save">{saving ? 'Saving...' : 'Save'}</Button>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={startEdit} className="gap-1.5" data-testid="tenant-modal-edit">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Save Changes?</AlertDialogTitle><AlertDialogDescription>Are you sure you want to save the changes to {tenant?.name}?</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmSave} data-testid="tenant-modal-confirm-save">Save</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
